import PropTypes from 'prop-types';
import {spacing, validSpacingValues, validBreakpointValues, SpacingType, BreakpointType} from '@nib-components/theme';

const validFlexWidthValues = ['content', '1/2', '1/3', '2/3', '1/4', '3/4', '1/5', '2/5', '3/5', '4/5', '1'] as const;
export type WidthType = typeof validFlexWidthValues[number];

const validTilesColumnsValues = [0, 1, 2, 3, 4, 5, 6] as const;
export type TilesColumnsType = typeof validTilesColumnsValues[number];

const validFlexAlignValues = ['center', 'right', 'left'] as const;
export type FlexAlignType = typeof validFlexAlignValues[number];
const validFlexVerticalAlignValues = ['center', 'top', 'bottom', 'stretch'] as const;
export type FlexVerticalAlignType = typeof validFlexVerticalAlignValues[number];

export type Nullable<T> = T | null;

type PartialRecord<K extends keyof never, T> = {
  [P in K]?: T;
};

export type ResponsiveProp<T> = PartialRecord<BreakpointType, T>;
export type ResponsiveOrStaticProp<T> = T | ResponsiveProp<T>;

export type ResponsiveSpaceProp = ResponsiveOrStaticProp<Nullable<SpacingType>>;

const spacePropType = PropTypes.oneOf(validSpacingValues);
const tilesColumnsPropType = PropTypes.oneOf(validTilesColumnsValues);
const flexWidthsPropType = PropTypes.oneOf(validFlexWidthValues);
const flexAlignmentPropType = PropTypes.oneOf(validFlexAlignValues);
const flexVerticalAlignmentPropType = PropTypes.oneOf(validFlexVerticalAlignValues);

export const breakpointPropTypes = PropTypes.oneOf(validBreakpointValues);

export const isValidSpacing = (value: number): number | boolean => value || value === 0;

export const getSpacingValue = (value: SpacingType): string => spacing[value];

export const isList = (el: string): boolean => ['ul', 'ol'].includes(el);

export const mapFlexWidths = (width: WidthType): any => {
  switch (width) {
    case '1/2':
      return (1 / 2) * 100;
    case '1/3':
      return (1 / 3) * 100;
    case '2/3':
      return (2 / 3) * 100;
    case '1/4':
      return (1 / 4) * 100;
    case '3/4':
      return (3 / 4) * 100;
    case '1/5':
      return (1 / 5) * 100;
    case '2/5':
      return (2 / 5) * 100;
    case '3/5':
      return (3 / 5) * 100;
    case '4/5':
      return (4 / 5) * 100;
  }
};

export const mapColumnstoFlexBasis = (columns: number): string => {
  switch (columns) {
    case 1:
    default:
      return `flex: 0 0 100%;`;
    case 2:
      return `flex: 0 0 50%;`;
    case 3:
      return `flex: 0 0 33.3333333333%;`;
    case 4:
      return `flex: 0 0 25%;`;
    case 5:
      return `flex: 0 0 20%;`;
    case 6:
      return `flex: 0 0 16.6666666667%;`;
  }
};

export const mapFlexAlignment = (flexAlignment: FlexAlignType | FlexVerticalAlignType): string => {
  if (flexAlignment === 'center') {
    return 'center';
  } else if (flexAlignment === 'right' || flexAlignment === 'bottom') {
    return 'flex-end';
  } else if (flexAlignment === 'left' || flexAlignment === 'top') {
    return 'flex-start';
  } else {
    return 'stretch';
  }
};

export const spacingPropTypeMap = PropTypes.oneOfType([
  spacePropType,
  PropTypes.shape({
    xs: spacePropType,
    sm: spacePropType,
    md: spacePropType,
    lg: spacePropType,
    xl: spacePropType,
    xxl: spacePropType,
    xxxl: spacePropType
  })
]);

export const tilesColumnsPropTypeMap = PropTypes.oneOfType([
  tilesColumnsPropType,
  PropTypes.shape({
    xs: tilesColumnsPropType,
    sm: tilesColumnsPropType,
    md: tilesColumnsPropType,
    lg: tilesColumnsPropType,
    xl: tilesColumnsPropType,
    xxl: tilesColumnsPropType,
    xxxl: tilesColumnsPropType
  })
]);

export const flexWidthsPropTypeMap = PropTypes.oneOfType([
  flexWidthsPropType,
  PropTypes.shape({
    xs: flexWidthsPropType,
    sm: flexWidthsPropType,
    md: flexWidthsPropType,
    lg: flexWidthsPropType,
    xl: flexWidthsPropType,
    xxl: flexWidthsPropType,
    xxxl: flexWidthsPropType
  })
]);

export const flexAlignmentPropTypeMap = PropTypes.oneOfType([
  flexAlignmentPropType,
  PropTypes.shape({
    xs: flexAlignmentPropType,
    sm: flexAlignmentPropType,
    md: flexAlignmentPropType,
    lg: flexAlignmentPropType,
    xl: flexAlignmentPropType,
    xxl: flexAlignmentPropType,
    xxxl: flexAlignmentPropType
  })
]);

export const flexVerticalAlignmentPropTypeMap = PropTypes.oneOfType([
  flexVerticalAlignmentPropType,
  PropTypes.shape({
    xs: flexVerticalAlignmentPropType,
    sm: flexVerticalAlignmentPropType,
    md: flexVerticalAlignmentPropType,
    lg: flexVerticalAlignmentPropType,
    xl: flexVerticalAlignmentPropType,
    xxl: flexVerticalAlignmentPropType,
    xxxl: flexVerticalAlignmentPropType
  })
]);
